// kernel/useAgents.ts
import { getProjectStudioViewMode } from '#lib/studio-project-view';
import { listPromptUploadOwner } from '#lib/list/listPromptUploadOwner';
import { useCallback, RefObject, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { uploadFileToCloud } from '#lib/azure-client';
import { KernelHandle, useAgentsReturnType, AgentProjectContext, LlmTurnImage, Message, ScriptInstance, UiCommand, ErrorPayload, ScriptDefinition, AssistRequestBody, AssistantTurnDebugInfo, AgentMode, DealerExecutionOptions, ImageAttachment, ChatSession, ActiveEntityFocus, SessionFocusRuntimeMap, GenerateOriginalConceptsRequestBody, GenerateOriginalConceptsResponse, DesignerBrainId } from '#types/types';
import { unpromptedChainMayContinue } from '#lib/leasing/unprompted-turns';
import { describeMandate } from '#lib/leasing/sourcingMandate';
import { getFileExtension } from '#lib/utils';
import { fetchApi } from './apiFetch';
import { COMMAND_DEFINITIONS } from '@/components/agentCommands';
import { buildAssetModelContext } from '@/components/asset-management/assetModelContext';
import { buildAnalystContext, buildAnalystTablesIndex } from '@/components/tables/tablesAgentContext';
import { buildSourceContext } from '@/components/source/sourceAgentContext';
import { buildFocusedBrandBlock, buildSourceShortlistContext, resolveOpenSourceShortlist } from '@/components/source/sourceShortlistContext';
import { resolveSourceFocusedBrandId, resolveSourceSelectedBrandIds } from '#lib/leasing/sourceFocusedBrand';
import { isAnalystViewOpen, useTablesStore } from '@/components/tables/tablesStore';
import { buildPulseContext } from '@/components/footfall/pulseAgentContext';
import { startReviewAfterResearch, useSlidesStore } from '@/components/slides/slidesStore';
import { RESEARCH_COMMANDS } from '#lib/slides/brandResearch';
import { buildSlidesAgentTurnContext } from '@/components/slides/slidesAgentContext';
import { projectIdOfSession, resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { isSourceAgentSession } from '#lib/leasing/sourceAgentMode';
import {
  LEGACY_COMPAT_USER_ACTIVITIES,
  normalizeUserActivities,
  POST_SOURCING_COMMAND_ACTIONS,
  SOURCE_ONLY_COMMAND_ACTIONS,
} from '#lib/user-activities';
import { isScriptExecuting } from '@/kernel/useCommands';
import {
  emitAssistantMessageEventFromSession,
  evaluateDefinitionLockState,
  getCommandsForMode,
  getTruncatedChatHistory,
  markRuntimeErrorsReported,
  patchSessionRuntimeIfAbortControllerOwner,
  removeMediaGenerationCommandsForPromptRewrite,
  removePlanningCommandsForAnswerOnlyRequest,
  resolveMessageTargetFromSession,
  snapshotRuntimeErrorsForAgent,
  takeQueuedMessage,
} from '@/kernel/agentTurnShared';
import { useLegacyDesigner } from '@/kernel/designer/legacy/useLegacyDesigner';
import { useJnnDesigner } from '@/kernel/designer/jnn/useJnnDesigner';
import { designerBrainOfSession } from '#lib/designer/brains';
import { recallLimit } from '#lib/slides/reviewRecall';

const MAX_ERROR_LOOPBACKS = 6;


export const PARALLELIZABLE_COMMANDS = new Set(['QUERY', 'WEB_SEARCH']);

// NOTE: opening a table (ANALYST_OPEN_TABLE) used to force auto-recall OFF so the agent
// couldn't loop re-opening it. That also stopped legitimate continuation ("open it AND read
// it"). The runaway re-open is now prevented precisely in useCommands (a script is not
// auto-recalled when it is byte-identical to the one that just ran), so opening a table now
// keeps the agent working, it continues to read/act on the table it just surfaced.

interface useAgentsProps {
  kernel: RefObject<KernelHandle | null>;
}

// Studio is mode-less: a Project-focused session is ALWAYS the Studio agent
// (director). The old preferDealerOverDirectorForProjectFocus escape hatch is
// gone, per-turn intelligence now lives in the task-family model router
// (lib/studioModelRouter.ts), not in a persona switch.
const resolveSessionSubjectAgentMode = (
  session: ChatSession | null | undefined
): AgentMode | null => {
  // Slides sessions focus a Project too (the deck's container), but they run
  // the Reviewer brain, never the Studio Designer. Identity beats subject.
  if (session?.creationModule === 'slides') return 'slides';
  if (session?.creationModule === 'pulse') return 'pulse';
  const subject = session?.activeSubject;
  if (!isEntityFocusSubject(subject)) return null;
  return subject.entityType === 'Project' ? 'director' : 'commander';
};

// Scope-default brain ("agents live in projects"): when a session has no active
// subject to drive the per-turn brain, its immutable creation scope is the
// structured default, a studio-born session runs the Designer even before its
// Project subject is (re)set. This is a FALLBACK, ranked below an explicit
// caller-supplied mode: subject can wander, but the session's home is where it
// lands by default. Analyst is intentionally NOT defaulted here, analyst turns
// are driven at execution by isAnalystViewOpen() (the standalone tool being
// open), not by session identity, and source/global keep the dealer default.
const resolveSessionScopeDefaultAgentMode = (
  session: ChatSession | null | undefined
): AgentMode | null => {
  // Master sessions are a SEPARATE construct: they run the orchestrator brain,
  // never a specialist. This is identity, not a fallback, a master session is
  // always the Master (it has no subject to wander to).
  if (session?.creationModule === 'master') return 'master';
  // Doctrine sessions run the DICTATOR brain (the dedicated doctrine agent that
  // reads/structures/edits the doctrine). They ride the dealer transport but
  // render the Doctrine view with a side chat, not the MasterConsole.
  if (session?.creationModule === 'doctrine') return 'doctrine';
  if (session?.creationModule === 'studio') return 'director';
  return null;
};

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const cloneJsonSafeRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  try {
    const cloned = JSON.parse(JSON.stringify(value));
    return cloned && typeof cloned === 'object' && !Array.isArray(cloned)
      ? (cloned as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
};

const normalizeSessionFocusRuntimeMap = (value: unknown): SessionFocusRuntimeMap | undefined => {
  const source = toRecord(value);
  const entries = Object.entries(source);
  if (entries.length === 0) return undefined;

  const normalized: SessionFocusRuntimeMap = {};
  for (const [rawKey, rawEntry] of entries) {
    const key = typeof rawKey === 'string' ? rawKey.trim() : '';
    if (!key) continue;

    const entry = toRecord(rawEntry);
    const focusScope = entry.focusScope === 'entity' || entry.focusScope === 'table'
      ? entry.focusScope
      : null;
    const entityType = typeof entry.entityType === 'string' ? entry.entityType.trim() : '';
    if (!focusScope || !entityType) continue;

    const sortBy = typeof entry.sortBy === 'string' && entry.sortBy.trim().length > 0
      ? entry.sortBy.trim()
      : undefined;
    const sortDir = entry.sortDir === 'asc' || entry.sortDir === 'desc'
      ? entry.sortDir
      : undefined;
    const pageSize =
      typeof entry.pageSize === 'number' && Number.isFinite(entry.pageSize)
        ? Math.max(1, Math.min(500, Math.floor(entry.pageSize)))
        : undefined;
    const visibleIds = Array.isArray(entry.visibleIds)
      ? Array.from(
          new Set(
            entry.visibleIds
              .filter((id): id is string => typeof id === 'string')
              .map((id) => id.trim())
              .filter((id) => id.length > 0)
          )
        ).slice(0, 200)
      : undefined;
    const totalVisible =
      typeof entry.totalVisible === 'number' && Number.isFinite(entry.totalVisible)
        ? Math.max(0, Math.floor(entry.totalVisible))
        : undefined;
    const updatedAt =
      typeof entry.updatedAt === 'number' && Number.isFinite(entry.updatedAt) && entry.updatedAt > 0
        ? entry.updatedAt
        : Date.now();
    const tableView =
      typeof entry.tableView === 'string' && entry.tableView.trim().length > 0
        ? entry.tableView.trim()
        : undefined;
    const presentationMode =
      entry.presentationMode === 'table' || entry.presentationMode === 'grid'
        ? entry.presentationMode
        : undefined;
    const filters = cloneJsonSafeRecord(entry.filters);

    normalized[key] = {
      focusScope,
      entityType: entityType as any,
      ...(tableView ? { tableView: tableView as any } : {}),
      ...(presentationMode ? { presentationMode } : {}),
      ...(filters ? { filters } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(sortDir ? { sortDir } : {}),
      ...(typeof pageSize === 'number' ? { pageSize } : {}),
      ...(visibleIds ? { visibleIds } : {}),
      ...(typeof totalVisible === 'number' ? { totalVisible } : {}),
      updatedAt,
    };
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

export function useAgents({
  kernel
}: useAgentsProps): useAgentsReturnType {

  const errorLoopbackCountRef = useRef(0);
  const successLoopbackCountRef = useRef(0);
  const getProjectIdFromSessionId = useCallback((sessionId: string): string | null => {
    const k = kernel.current;
    if (!k) return null;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return null;
    return projectIdOfSession(k.getInventory().ChatSession.registry[normalizedSessionId]);
  }, [kernel]);

  const { executeLegacyTurn, resolveDetachedState } = useLegacyDesigner({ kernel, getProjectIdFromSessionId });
  const { executeJnnTurn } = useJnnDesigner({ kernel, getProjectIdFromSessionId });
  const designerTurns = useMemo<Record<DesignerBrainId, typeof executeLegacyTurn>>(
    () => ({ legacy: executeLegacyTurn, jnn: executeJnnTurn }),
    [executeLegacyTurn, executeJnnTurn],
  );

// A dealer turn that fails re-runs itself this many times before surfacing the error (SRC-034),
// pausing longer each time so a provider blip and a real outage read differently.
const DEALER_TURN_MAX_RETRIES = 3;
const DEALER_TURN_RETRY_BACKOFF_MS = [2_000, 6_000, 15_000] as const;

  const _executeDealerTurn = useCallback(async (
    sessionId: string,
    prompt: string,
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: DealerExecutionOptions
  ): Promise<void> => {
    if (!kernel.current) {
        console.error("[useAgents] EditorRef is not available.");
        return;
    }
    takeQueuedMessage(kernel.current, sessionId);
    // Who started this turn, written at the START of EVERY turn so it always describes the one now
    // running. Deliberately not a flag someone else has to remember to clear: the first shape of
    // this fix was cleared by the setup panel on close, which lowered it on whichever session
    // happened to be active by then and stranded the ban on the other one. Rewriting it here means
    // an unrelated later turn (a run's closing verdict, a quickstart) cannot inherit it at all.
    // `skipUserMessage` cannot stand in for this: askAssistant posts the user's message itself and
    // then calls this function with skipUserMessage: true, so a typed message looks identical to a
    // recall. Only the caller knows, so the caller says. Rules: lib/leasing/unprompted-turns.ts.
    const previousUnpromptedDepth = kernel.current.readEntityRuntimeState('ChatSession', sessionId).unpromptedTurnDepth ?? 0;
    const unpromptedTurnDepth = options?.continuesUnpromptedTurn ? previousUnpromptedDepth + 1 : 0;
    // WHO STARTED THE CHAIN, carried across continuations. A turn the user typed is not the turn
    // that answers them: the agent runs a command, wakes itself through triggerReEvaluation, and
    // reads the results in a SECOND turn that nobody typed. That second turn is where the time
    // actually goes, so gating the thinking line on `userTypedThePrompt` alone left the longest
    // part of every Source answer in silence - "Queried 1 entity", then nothing. Reported 2026-08-16.
    //
    // A fresh turn nobody typed clears it, which is what keeps a panel reaction from thinking.
    const chainStartedByUser = options?.userTypedThePrompt === true || options?.showThinking === true
      ? true
      : options?.continuesTurn === true
        ? kernel.current.readEntityRuntimeState('ChatSession', sessionId).chainStartedByUser === true
        : false;
    const isSlidesButtonTurn = kernel.current.getInventory().ChatSession.registry[sessionId]?.creationModule === 'slides'
      && options?.showThinking === true && options?.skipUserMessage === true && options?.userTypedThePrompt !== true;
    const slidesButtonIntent = isSlidesButtonTurn
      ? prompt
      : options?.continuesTurn || options?.retryAttempt
        ? kernel.current.readEntityRuntimeState('ChatSession', sessionId).slidesButtonIntent ?? null
        : null;
    kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { unpromptedTurnDepth, chainStartedByUser, slidesButtonIntent });

    // WHAT THE USER TYPED FOR THIS TURN, which is what a billed command checks before it spends
    // anything (assertUserAskedToLaunch, components/dealerCommands.ts). Rewritten on EVERY turn for
    // the same reason the depth above is: a value someone else has to remember to clear is a value
    // that gets inherited by a turn it does not describe.
    //
    // Only the caller knows, and it says so here. It used to be read off `skipUserMessage`, which is
    // why launching from the chat was dead: that flag means "the message is already in the
    // transcript", not "no human spoke". askAssistant posts the user's message itself and then calls
    // this with skipUserMessage: true, so EVERY Source chat turn recorded an empty prompt and no
    // person could ever satisfy the guard. askAssistant's OWN skipUserMessage does carry the answer,
    // and that is what it forwards now.
    //
    // THE DEPTH ABOVE CANNOT STAND IN FOR THIS EITHER. Depth 0 does not mean "a human started this
    // turn", it means "this is not the setup panel's chain": the campaign-landing recall in
    // SourcingCenterView.tsx wakes the agent on a socket event and scores depth 0. So a turn nobody
    // asked for keeps its own words empty here, whatever its depth.
    // The words that STARTED the chain, carried across its continuations. Only the contacts guard
    // reads it: /fill-data asks for contacts, but its contacts step runs passes later, in a turn
    // nobody typed, and was refused (preview, 2026-09-26). The launch guard stays on this turn alone.
    const lastUserChainPrompt = options?.userTypedThePrompt
      ? (prompt ?? '')
      : options?.continuesTurn === true && chainStartedByUser
        ? kernel.current.getState?.().lastUserChainPrompt ?? ''
        : '';
    kernel.current.setState({ lastUserTurnPrompt: options?.userTypedThePrompt ? (prompt ?? '') : '', lastUserChainPrompt });
    const isFactoryCommandTurn = /^\s*\/factory\b/i.test(prompt ?? '');

    // A TURN A PERSON TYPED SAYS SO IMMEDIATELY, and it asks the flag directly above, not
    // `skipUserMessage`. That was the bug: skipUserMessage means "the message is already in the
    // transcript", and askAssistant hardcodes it true for EVERY Source chat turn, so gating the
    // thinking line on it deleted the line from the whole module. Reported on 2026-08-16 as "you
    // basically completely deleted the Thinking animation", and the note above this line had
    // already warned, in writing, that this flag cannot answer this question.
    //
    // It fires here rather than before the fetch because everything between the two is the turn
    // being assembled - relational context, doctrine, history, images - and that is precisely the
    // silence the user was complaining about.
    if (chainStartedByUser && !isFactoryCommandTurn) kernel.current.startThinkingSequence?.(sessionId);

    const currentAssistantAbortController = new AbortController();
    const candidateReview = useSlidesStore.getState().getAgentRun(sessionId);
    const reviewRunForTurn = candidateReview && (options?.continuesTurn || options?.retryAttempt || options?.slidesReviewRunId === candidateReview.id || options?.slidesProposalRunId === candidateReview.id)
      ? candidateReview : null;
    let researchRun = useSlidesStore.getState().brandResearchRun;
    if (researchRun?.sessionId !== sessionId) researchRun = null;
    if (researchRun?.phase === 'complete' && options?.userTypedThePrompt) {
      useSlidesStore.getState().cancelBrandResearch();
      researchRun = null;
    }
    const researchRunForTurn = researchRun;
    const cancelResearchTurn = () => {
      const active = useSlidesStore.getState().brandResearchRun;
      if (active && active.id !== researchRunForTurn?.id) return;
      kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { isStopRequested: true });
      currentAssistantAbortController.abort();
    };
    const cancelResearch = () => {
      if (useSlidesStore.getState().brandResearchRun?.id === researchRunForTurn?.id) useSlidesStore.getState().cancelBrandResearch();
    };
    researchRunForTurn?.controller.signal.addEventListener('abort', cancelResearchTurn, { once: true });
    if (researchRunForTurn) currentAssistantAbortController.signal.addEventListener('abort', cancelResearch, { once: true });


    // ONE FAILED PROVIDER CALL MUST NOT KILL THE TASK (SRC-034). A dealer turn that dies mid-
    // stream or never answers re-runs ITSELF: same prompt, same options, bounded to three
    // attempts with a growing pause, telemetry in the session status so the user watches a retry
    // instead of a freeze. Past the cap the error surfaces loudly, and a turn the user stopped
    // (abort) never retries. `skipUserMessage` on the re-run: the user's message is already in
    // the transcript, a retry must not post it twice.
    let dealerTurnRetryScheduled = false;
    let dealerScriptStarted = false;
    let dealerTurnFailed = false;
    let dealerTurnIncomplete = false;
    const scheduleDealerTurnRetry = (): boolean => {
      const attempt = (options?.retryAttempt ?? 0) + 1;
      if (attempt > DEALER_TURN_MAX_RETRIES) return false;
      if (currentAssistantAbortController.signal.aborted) return false;
      if (reviewRunForTurn && useSlidesStore.getState().getAgentRun(sessionId)?.id !== reviewRunForTurn.id) return false;
      dealerTurnRetryScheduled = true;
      const delayMs = DEALER_TURN_RETRY_BACKOFF_MS[Math.min(attempt - 1, DEALER_TURN_RETRY_BACKOFF_MS.length - 1)] ?? 2_000;
      console.warn(`[useAgents] Dealer turn failed, retrying (${attempt}/${DEALER_TURN_MAX_RETRIES}) in ${delayMs}ms`);
      kernel.current?.setSessionStatus(sessionId, `Provider error, retrying (${attempt}/${DEALER_TURN_MAX_RETRIES})...`);
      const clearRetryFeedback = () => {
        const pending = kernel.current?.readEntityRuntimeState('ChatSession', sessionId);
        patchSessionRuntimeIfAbortControllerOwner(kernel.current, sessionId, currentAssistantAbortController, {
          abortController: null,
          slidesButtonIntent: null,
          chatState: (pending?.chatState ?? []).filter((step) => step.settled === true),
        });
      };
      currentAssistantAbortController.signal.addEventListener('abort', clearRetryFeedback, { once: true });
      setTimeout(() => {
        currentAssistantAbortController.signal.removeEventListener('abort', clearRetryFeedback);
        researchRunForTurn?.controller.signal.removeEventListener('abort', cancelResearchTurn);
        currentAssistantAbortController.signal.removeEventListener('abort', cancelResearch);
        const latest = kernel.current?.readEntityRuntimeState('ChatSession', sessionId);
        if ((reviewRunForTurn && useSlidesStore.getState().getAgentRun(sessionId)?.id !== reviewRunForTurn.id) ||
            currentAssistantAbortController.signal.aborted || latest?.isStopRequested ||
            latest?.abortController !== currentAssistantAbortController) {
          clearRetryFeedback();
          return;
        }
        // Through the kernel handle rather than a direct self-call: the binding is still being
        // declared at this point of the file, and the handle exposes the same turn.
        void kernel.current?.executeSystemDealerTurn(sessionId, prompt, onScriptComplete, {
          ...options,
          skipUserMessage: true,
          retryAttempt: attempt,
        });
      }, delayMs);
      return true;
    };

    kernel.current.patchEntityRuntimeState('ChatSession', sessionId, {
        abortController: currentAssistantAbortController,
        isStopRequested: false
    });

    try {
      const skipUserMessage = options?.skipUserMessage ?? false;
      const attachedImageUrlsForTurn = options?.attachedImageUrls ?? [];
      const ignoreScripts = options?.ignoreScripts ?? false;

      const session = kernel.current.getInventory().ChatSession.registry[sessionId];
      const messages = session ? kernel.current.getChatSessionMessages(session.id) : [];
      const activeAnchors = session?.workingMemory?.anchors || [];
      const activeSubjectCandidate = session?.activeSubject ?? null;
      const activeSubject = isEntityFocusSubject(activeSubjectCandidate)
        ? activeSubjectCandidate
        : null;
      // A master session rides this same transport but is a SEPARATE brain: it
      // gets the orchestration toolset and mode:'master' (the server swaps in the
      // orchestrator persona). It never gets the specialist CRM/design tools.
      // Doctrine sessions run the same Master brain (they exist so the user can
      // ask the agent to fill in their doctrine files).
      const isMasterTurn = session?.creationModule === 'master' || session?.creationModule === 'doctrine';
      // A doctrine session runs the Dictator (its own mode + doctrine toolset); it
      // rides the same transport as Master (hence isMasterTurn stays true) but
      // sends mode:'doctrine' and gets the doctrine-editing commands.
      const isDoctrineTurn = session?.creationModule === 'doctrine';
      // Slides sessions run the Reviewer brain on this same transport, with the
      // SLIDES_* toolset and the live deck context (evaluated fresh every pass so
      // the persona persists across a multi-pass review).
      const isSlidesTurn = session?.creationModule === 'slides';
      // Pulse sessions run the plan editor's agent, with the PULSE_* toolset and the open plan as context.
      const isPulseTurn = session?.creationModule === 'pulse';

      const normalizedMessages = messages.map((message: Message) => {
          if ((!message.imageUrls || message.imageUrls.length === 0) && Array.isArray(message.images) && message.images.length > 0) {
            const derivedUrls = Array.from(new Set(message.images
              .map((attachment: ImageAttachment) => attachment?.url || attachment?.originalUrl)
              .filter((url: string | undefined): url is string => typeof url === 'string' && url.length > 0)));
            if (derivedUrls.length > 0) {
              return { ...message, imageUrls: derivedUrls };
            }
          }
          return message;
        });
        const userPreferences = kernel.current.getUserPreferences();

        if (!skipUserMessage) {
            kernel.current.addUserMessage(prompt, attachedImageUrlsForTurn);
        }

        const normalizedActivities = normalizeUserActivities(userPreferences.activities, {
          fallback: LEGACY_COMPAT_USER_ACTIVITIES,
        });
        const canAccessSource = normalizedActivities.includes('SOURCE');

        // The Analyst is a distinct agent that rides the Dealer transport. When the user
        // is working in the Analyst tool, this turn runs the Analyst brain (the server swaps
        // the knowledge base on mode:'analyst') with a curated, finance-only toolset, it must
        // NOT see the leasing CRM tools. Evaluated fresh every turn (incl. each autoRecall
        // pass) so the Analyst persona persists across a multi-pass model build.
        const isAnalystTurn = !isPulseTurn && isAnalystViewOpen();
        // Drive the "agent working" glow on the Analyst grid for the whole build: stamp activity
        // at every analyst turn start (it lingers a few seconds, refreshed each pass + on writes).
        if (isAnalystTurn) useTablesStore.getState().markAgentActive();

        // THE OPEN SHORTLIST WINS OVER THE PORTFOLIO DEFAULT.
        // Entering Source stamps the session subject with the org's default portfolio, and opening
        // an asset shortlist from the dashboard only sets `sourceSelectedCampaignId`, it never
        // re-stamps the subject. The Dealer was therefore told "TARGET SUBJECT: Portfolio ALTAREA"
        // (and handed ALTAREA as a mass-10 forced anchor) while the user was reading one asset's
        // list, which is how a remark about that asset came back as "UPDATE Portfolio - ALTAREA".
        // Resolve what is really open and let it win, FOR THIS TURN ONLY: nothing here is persisted
        // and the UI's own subject is untouched, so the portfolio remains the subject the moment the
        // user closes the shortlist.
        // Only a SOURCE session claims the open shortlist. `sourceSelectedCampaignId` is global
        // kernel state and survives navigating away from Source, so without this a Studio or global
        // chat could inherit an asset subject it has nothing to do with. Same predicate the
        // auto-recall gate in useCommands and the Source mode picker use, deliberately.
        // RESOLVED HERE, BEFORE THE TOOLSET IS BUILT, and not next to the context blocks further
        // down: command lock evaluation reads the subject too, so resolving it later left the locks
        // judging the stale portfolio: an asset command came back "[LOCKED] Focus a Property...
        // first." on a shortlist opened from the dashboard, and the agent fell back to a raw MUTATE.
        const isSourceSession = isSourceAgentSession(session);
        const isDealerTurn = !isMasterTurn && !isSlidesTurn && !isAnalystTurn && !isPulseTurn;
        // The turn's own campaign binding outranks the globally open one: a session bound to
        // campaign A reasons about A even if the user has since put campaign B on screen.
        const sessionSubjectForBinding = session?.activeSubject ?? null;
        const sessionBoundCampaignId =
          isEntityFocusSubject(sessionSubjectForBinding) &&
          typeof sessionSubjectForBinding.campaignId === 'string'
            ? sessionSubjectForBinding.campaignId
            : null;
        const openSourceShortlist =
          isDealerTurn && isSourceSession
            ? resolveOpenSourceShortlist(kernel.current, sessionBoundCampaignId)
            : null;
        // The ONE subject this turn runs on: locks, the prompt's TARGET SUBJECT and the session
        // context block must all read the same thing or the agent is offered a toolset that does not
        // match the subject it is told to act on. Falls straight back to the session's own subject,
        // so a turn with genuinely nothing open is locked exactly as it was before.
        const turnActiveSubject: ActiveEntityFocus | null = openSourceShortlist
          ? {
              entityType: 'Property' as const,
              entityId: openSourceShortlist.propertyId,
              focusActionType: 'ORCHESTRATE_SOURCING' as const,
            }
          : activeSubject;

        // Web search is always available, in every module and tool, there is
        // no user-facing toggle any more. The agent still only invokes
        // WEB_SEARCH when the query actually implies a search.
        const internalDbEnabled = kernel.current?.getInternalDbEnabled() ?? true;
        const commandsForThisTurn = getCommandsForMode(
          isSlidesTurn ? 'slides' : isPulseTurn ? 'pulse' : isDoctrineTurn ? 'doctrine' : isMasterTurn ? 'master' : 'commander',
        )
          .filter((cmd) => !researchRunForTurn || (researchRunForTurn.phase !== 'complete' && RESEARCH_COMMANDS.has(cmd.action_type)))
          .filter((cmd) => canAccessSource || !SOURCE_ONLY_COMMAND_ACTIONS.has(cmd.action_type))
          .filter((cmd) => !POST_SOURCING_COMMAND_ACTIONS.has(cmd.action_type))
          .filter((cmd) => internalDbEnabled || cmd.action_type !== 'QUERY')
          .map(cmd => {
            const def = COMMAND_DEFINITIONS[cmd.action_type];
            const lockState = evaluateDefinitionLockState(def, {
              kernel,
              activeSubject: turnActiveSubject,
              anchors: activeAnchors,
              activeProjectId: getProjectIdFromSessionId(sessionId),
            });
            if (lockState?.isLocked) {
                return {
                    ...cmd,
                    description: `[LOCKED] ${lockState.unlockHint}`,
                    parameters: [],
                    isLocked: true,
                    unlockHint: lockState.unlockHint
                };
            }
            // An explicit unlock is final: MPRACE would otherwise re-lock a Brand command whose
            // brand is not in the data graph and hide its parameters (ADD_BRAND_STORES on /fill-stores).
            return lockState ? { ...cmd, isLocked: false } : cmd;
          });

        // A Slides session is born inside the deck's container Project and never
        // leaves it, but resolveProjectIdFromChatSessionContext (which is what
        // getProjectIdFromSessionId ends in) only trusts a session's creation
        // entity for `studio`. So every Slides turn resolved to null, fell back to
        // a synthetic `dealer-<ts>` id, and the assist route skipped project
        // attribution: no GenerationLog row ever carried a deck and per-deck spend
        // was unreadable.
        //
        // Read the creation entity HERE rather than widening the shared resolver:
        // its other consumer, selectActiveSessionProjectIdFromInventoryState, is
        // what AppUiLayer's global shortcut handler uses to decide it may run a
        // Studio slash command, and it treats "resolves to a project" as "a Studio
        // canvas context exists". Slides mounts no #editor-canvas, so widening the
        // resolver makes Ctrl+Z on a deck fire Studio's undo against the deck's
        // Project. Teaching that handler the difference is a Studio change, not a
        // Slides one.
        const slidesHomeProjectId =
          isSlidesTurn && session?.creationEntityType === 'Project'
            ? (typeof session.creationEntityId === 'string' ? session.creationEntityId.trim() : '') || null
            : null;
        const resolvedProjectId = getProjectIdFromSessionId(sessionId) ?? slidesHomeProjectId;
        const projectNameFromDto = resolvedProjectId
          ? kernel.current.getInventory().Project.registry[resolvedProjectId]?.name
          : null;
        const projectId = resolvedProjectId ?? `dealer-${Date.now()}`;
        const projectName =
          typeof projectNameFromDto === 'string' && projectNameFromDto.trim().length > 0
            ? projectNameFromDto.trim()
            : 'Dealer Session';
        const projectContext: AgentProjectContext = {
            projectId,
            projectName,
            activeWorkspaceId: '',
            generationRect: { x: 0, y: 0, w: 0, h: 0 },
            workspaces: [],
        };

        const truncatedChatHistory = getTruncatedChatHistory(normalizedMessages);
        const MAX_USER_IMAGES = 20;
        const userAttachedImages: LlmTurnImage[] = [];
        const seenUserAttachmentUrls = new Set<string>();

        attachedImageUrlsForTurn.forEach(url => {
          if (url && !seenUserAttachmentUrls.has(url)) {
            userAttachedImages.push({
              url,
              label: 'User-attached image (current turn)',
            });
            seenUserAttachmentUrls.add(url);
          }
        });

        if (userAttachedImages.length < MAX_USER_IMAGES) {
          let userTurnsAgo = 0;
          for (const msg of [...normalizedMessages].reverse()) {
            if (userAttachedImages.length >= MAX_USER_IMAGES) break;
            if (msg.role !== 'user') continue;
            userTurnsAgo += 1;
            if (!msg.imageUrls || msg.imageUrls.length === 0) continue;
            for (const url of msg.imageUrls) {
              if (userAttachedImages.length >= MAX_USER_IMAGES) break;
              if (url && !seenUserAttachmentUrls.has(url)) {
                const promptSnippet = (msg.content || '').substring(0, 75);
                userAttachedImages.push({
                  url,
                  label: `User-attached image (from ${userTurnsAgo} user turn(s) ago, with prompt: "${promptSnippet}...")`,
                });
                seenUserAttachmentUrls.add(url);
              }
            }
          }
        }

        const lastUserMessage = [...normalizedMessages].reverse().find(msg => msg.role === 'user');
        const lastAssistantMessage = [...normalizedMessages].reverse().find(msg => msg.role === 'assistant');

        const chatSessionContext = (() => {
          if (!session) return null;
          const sessionRuntime = kernel.current?.readEntityRuntimeState('ChatSession', session.id);
          return {
            id: session.id,
            name: session.name,
            messageCount: kernel.current.getChatSessionMessages(session.id).length,
            createdAt: session.createdAt,
            updatedAt: session.updatedAt ?? null,
            lastUserMessageAt: lastUserMessage?.timestamp ?? null,
            lastAssistantMessageAt: lastAssistantMessage?.timestamp ?? null,
            // Same resolution the toolset was locked against, so the subject the agent is TOLD to
            // act on and the commands it is OFFERED can never disagree. The no-shortlist branch
            // keeps the raw session subject on purpose: a table-scope focus is not an entity focus
            // and must survive into the prompt.
            activeSubject: openSourceShortlist ? turnActiveSubject : session.activeSubject ?? null,
            subjectHistory: Array.isArray(session.subjectHistory)
              ? session.subjectHistory.slice(-120)
              : undefined,
            workingMemory: session.workingMemory || null,
            focusRuntimeByKey: normalizeSessionFocusRuntimeMap(sessionRuntime?.focusRuntimeByKey),
          };
        })();

        // Seed the agent with a snapshot of the focused asset's live model, so
        // it can act on relative instructions and edit existing cells/formulas.
        let assetModelContext: string | null = null;
        try {
          const subj = session?.activeSubject;
          if (subj && subj.entityType === 'Property' && typeof subj.entityId === 'string') {
            const inv = kernel.current?.getInventory();
            const property = inv?.Property?.registry?.[subj.entityId] ?? null;
            const unitIds: string[] = Array.isArray(property?.unitIds) ? property.unitIds : [];
            const units = unitIds.map((id) => inv?.Unit?.registry?.[id]);
            assetModelContext = buildAssetModelContext(
              property,
              units,
              kernel.current?.readEntityRuntimeState('Property', subj.entityId).assetModel ?? {},
            );
          }
        } catch {
          /* model seeding is best-effort and must never break a turn */
        }

        // Seed the agent with the open Analyst table (if any) so it can read and
        // edit the exact spreadsheet the user is looking at.
        let analystContext: string | null = null;
        let analystTablesIndex: string | null = null;
        try {
          analystContext = buildAnalystContext({ forMaster: isMasterTurn, sessionId, properties: kernel.current?.getState().inventory.Property.registry });
          // List ALL Analyst tables so the agent can open the right one instead of
          // recreating it. Injected even when no table is open (does not force the model).
          analystTablesIndex = buildAnalystTablesIndex();
        } catch {
          /* best-effort; must never break a turn */
        }

        // Dealer/Source: the shortlist the user has open, with the EFFECTIVE rubric that really
        // governs it (the asset's stored criteria plus the launch-brief ones). Without it the agent
        // could not see the real criterion labels or weights, and answered a request to reweight by
        // inventing a tidy three-criterion rubric summing to 100. Same shape as the Analyst's open
        // table: live UI context, read once per turn, never fatal.
        let sourceShortlistContext: string | null = null;
        if (openSourceShortlist) {
          try {
            sourceShortlistContext = await buildSourceShortlistContext(kernel.current, openSourceShortlist);
          } catch {
            /* best-effort; must never break a turn */
          }
        }

        // What the user is pointing AT on the Source surface: the open brand, and the store whose
        // card is open on the map. Without this the user could say "this store doesn't exist" over
        // an open pin and the agent had 21 candidates and no way to choose, it answered by
        // querying blindly for the address. Same contract as the shortlist block: live UI state,
        // read once per turn, best-effort, never fatal.
        let sourceFocusContext: string | null = null;
        if (isDealerTurn && isSourceSession) {
          try {
            const state = kernel.current.getState();
            const inventory = kernel.current.getInventory();
            const lines: string[] = [];
            const focusedBrandId = resolveSourceFocusedBrandId(state as never);
            const focusedBrand = focusedBrandId
              ? (inventory.Brand.registry[focusedBrandId] as { name?: string } | undefined)
              : undefined;
            if (focusedBrandId && focusedBrand?.name) {
              lines.push(`- Brand: ${focusedBrand.name} (Brand id ${focusedBrandId})`);
            }
            // THE TICKED ROWS, EVERY TURN. The shortlist's checkboxes are the user's way of saying
            // "these ones", and the turn used to carry only the single expanded row, so a message
            // about five selected brands reached the agent with no idea which five: it either asked
            // which brands were meant or answered about the whole list. The selection is live kernel
            // state (sourceSelectedFitIds), so it is re-read here on every turn rather than captured
            // when the conversation started - tick a row after sending and the NEXT turn sees it.
            const selectedPairs = resolveSourceSelectedBrandIds(state as never);
            if (selectedPairs.length > 0) {
              // Bounded: a user can tick a 200-row shortlist, and the names alone would crowd out
              // the rest of the context. The count is always exact even when the list is cut.
              const shown = selectedPairs.slice(0, 25);
              const named = shown.map(({ brandId }) => {
                const brand = inventory.Brand.registry[brandId] as { name?: string } | undefined;
                return brand?.name ? `${brand.name} (Brand id ${brandId})` : `Brand id ${brandId}`;
              });
              lines.push(
                `- Rows SELECTED in the shortlist (${selectedPairs.length}): ${named.join('; ')}`
                  + (selectedPairs.length > shown.length ? `; and ${selectedPairs.length - shown.length} more` : ''),
              );
              lines.push(
                '  "these", "ces enseignes", "ceux-la", "the selected ones", "this selection" in the user\'s'
                  + ' next message mean THESE rows, not the whole shortlist. A command that takes brandIds'
                  + ' takes these ids. If the user asks for something on "the list" with rows ticked and you'
                  + ' cannot tell which they mean, ask before spending a credit.',
              );
            }
            const storeId = typeof state.sourceFocusedStoreId === 'string' ? state.sourceFocusedStoreId : '';
            const store = storeId
              ? (inventory.BrandLocation?.registry?.[storeId] as
                  | { name?: string; address?: string; city?: string; postalCode?: string; country?: string }
                  | undefined)
              : undefined;
            if (storeId) {
              const where = [store?.address, store?.postalCode, store?.city, store?.country]
                .filter((part) => typeof part === 'string' && part.trim().length > 0)
                .join(', ');
              lines.push(
                `- Store card open: ${store?.name || 'store'}${where ? `, ${where}` : ''} (BrandLocation id ${storeId})`,
              );
              lines.push('  "this store", "this one", "it" in the user\'s next message mean THIS BrandLocation.');
            }
            if (lines.length > 0) {
              // The panel's actual CONTENT, not just the brand's name. This block used to be built
              // only when an asset shortlist was open, so on a portfolio-fit shortlist the turn
              // carried "- Brand: JD Sports (Brand id ...)" and nothing else while the panel beside
              // it showed revenue, network size and the expansion series. It belongs here, where
              // the gate is "a brand is on screen", which is the condition it actually needs.
              const brandBlock = await buildFocusedBrandBlock(kernel.current);
              sourceFocusContext = ['--- WHAT THE USER IS LOOKING AT (Source) ---', ...lines].join('\n')
                + brandBlock;
            }
          } catch {
            /* best-effort; must never break a turn */
          }
        }

        // Master-only: a directory of the sourcing campaigns so the Master can resolve
        // names the user says to the explicit ids its Source commands need (a Master
        // session has no activeSubject). Nothing pre-loads Source data for a Master
        // turn, so pull the org's recent campaigns into inventory first (cached ~60s so
        // repeat turns don't re-fetch), then read them. Best-effort: fetch + read only,
        // never navigates, never breaks a turn.
        let sourceContext: string | null = null;
        if (isMasterTurn) {
          try {
            const ks = kernel.current;
            if (ks) {
              await Promise.all([
                // Portfolios + properties are the name->id directory the Master resolves
                // ("the Altarea shortlist" -> portfolioId). Campaigns are optional (a
                // direct campaignId route). All cached so repeat turns don't re-fetch.
                ks.queryInventory('Portfolio', { limit: 40, ttl: 120_000 }).catch(() => null),
                ks.queryInventory('Property', { limit: 100, ttl: 120_000 }).catch(() => null),
                // ONE campaign list at every granularity (app/api/inventory/campaigns), instead of
                // one query per legacy table: the granularity is a column on the row now, so the
                // directory reads it rather than inferring it from which registry it came out of.
                // 40 is the cap buildSourceContext prints, so a bigger page would only be dropped.
                ks.queryInventory('Campaign', { limit: 40, ttl: 60_000 }).catch(() => null),
              ]);
            }
            sourceContext = buildSourceContext(ks);
          } catch {
            /* best-effort; must never break a turn */
          }
        }

        // When the user is actually working in the Analyst (a table is open), use
        // the Analyst's per-tool default model (set in the global parameters tab),
        // falling back to Claude Opus 4.8, the strongest model wired in for
        // cross-sheet spreadsheet formulas / financial models. If the chosen
        // provider key is missing it auto-falls back via the assist route's
        // provider/fallback cascade. Everything else keeps the user's chosen model.
        // The Analyst model is resolved SERVER-SIDE by availability priority, Gemini > Claude > GPT,
        // first available, in the assist route (resolveAnalystModel), and the route's runtime fallback
        // cascades on if that provider errors. So the client no longer pins a single model: a blocked
        // provider (Gemini is hard-blocked until 2026-07-23) or an exhausted quota (Opus until ~07-01)
        // transparently falls through to the next instead of hard-failing. The user's chat-model
        // selection is ignored for Analyst turns (the route overrides it on mode:'analyst').
        const effectiveUserPreferences = userPreferences;

        // Auto-recall continuation turns carry an empty prompt (skipUserMessage), there is no new
        // user message, the agent is resuming its own multi-step build. Sending a literal "" makes
        // some models (Gemini especially) narrate "your message came through empty / was cut off".
        // Hand them an explicit internal continuation directive instead so they just continue.
        // Auto-recall continuations carry skipUserMessage + a blank prompt and no new images. Send a
        // MINIMAL non-empty token rather than "" so models (Gemini/GPT) don't narrate "your message
        // came through empty / was cut off", but keep it tiny so it doesn't hijack the model's
        // attention into prose/"final summary" instead of letting the build doctrine drive the next
        // command script (a verbose directive here stalled multi-step builds after the first turn).
        // A genuine image-only user send (askAssistant) also has skipUserMessage + blank prompt but
        // carries attachedImageUrls, so it is excluded and keeps the real (empty) prompt + its image.
        const promptForModel = slidesButtonIntent && prompt !== slidesButtonIntent
          ? `Continue the current Slides button request: ${slidesButtonIntent}\n\n${prompt || 'Continue.'}`
          : skipUserMessage && (!prompt || prompt.trim().length === 0) && attachedImageUrlsForTurn.length === 0
            ? 'Continue.'
            : prompt;

        // Slides turns: the live deck context (page outline, review state, focused
        // page) plus the focused/requested page snapshots as vision attachments ,
        // the Reviewer judges what it SEES, not just extracted text.
        let slidesContext: string | null = null;
        let slidesTurnImages: LlmTurnImage[] = [];
        let slidesGuidelineKey: string | undefined;
        if (isSlidesTurn) {
          try {
            const slidesTurn = await buildSlidesAgentTurnContext(reviewRunForTurn?.projectId ?? resolvedProjectId);
            slidesContext = slidesTurn.situation;
            slidesTurnImages = slidesTurn.images;
            slidesGuidelineKey = slidesTurn.guidelineKey;
          } catch {
            /* best-effort; must never break a turn */
          }
          // Surface the URLs of images the user attached THIS turn so the Reviewer
          // can PLACE them on a slide (e.g. a counter-proposal visual or a reference)
          // via an image element. The model already SEES them as vision inputs
          // (userAttachedImages), but it needs the URL as text to reference one.
          if (attachedImageUrlsForTurn.length > 0) {
            const attachedList = attachedImageUrlsForTurn.map((u) => `- ${u}`).join('\n');
            slidesContext = `${slidesContext ?? ''}\n\nUSER-ATTACHED IMAGES this turn (you can place one on a slide with SLIDES_ADD_ELEMENT type 'image', url set to the URL below; size the frame to the page for full-bleed):\n${attachedList}`;
          }
        }

        const mandateContext = describeMandate(kernel.current?.getState().sourceMandates?.[sessionId]);
        const assistBody: AssistRequestBody = {
            prompt: promptForModel,
            situation: isSlidesTurn
              ? (slidesContext ?? 'No deck is open yet. Ask the user to import a proposal PDF before reviewing.')
              : isPulseTurn
              ? buildPulseContext(kernel.current.getState(), session?.creationEntityId ?? null)
              : (isAnalystTurn || isMasterTurn
                ? '' // the Analyst/Master brains provide their own framing; only the live table/model data below is needed (the Master builds models with the same ANALYST_* commands and reads this snapshot back to verify)
                : 'You are in Dealer mode. The user is interacting with you for external intelligence tasks.') +
              // The open shortlist comes FIRST: it is the subject of the turn, everything below is
              // secondary workspace state.
              (sourceShortlistContext ? `\n\n${sourceShortlistContext}` : '') +
              (mandateContext ? `\n\n${mandateContext}` : '') +
              (sourceFocusContext ? `\n\n${sourceFocusContext}` : '') +
              (assetModelContext ? `\n\n${assetModelContext}` : '') +
              (analystContext ? `\n\n${analystContext}` : '') +
              (analystTablesIndex ? `\n\n${analystTablesIndex}` : '') +
              (sourceContext ? `\n\n${sourceContext}` : ''),
            available_commands: commandsForThisTurn,
            chatHistory: truncatedChatHistory,
            projectContext: projectContext,
            userPreferences: effectiveUserPreferences,
            mode: isSlidesTurn
              ? 'slides'
              : isPulseTurn
              ? 'pulse'
              : isAnalystTurn
                ? 'analyst'
                : isDoctrineTurn
                  ? 'doctrine'
                  : isMasterTurn
                    ? 'master'
                    : 'commander',
            chatSessionContext,
            systemContextImages: slidesTurnImages.length > 0 ? slidesTurnImages : undefined,
            slidesGuidelineKey,
            slidesResearchIntent: !!researchRunForTurn,
            slidesReviewRunId: isSlidesTurn && reviewRunForTurn && !('kind' in reviewRunForTurn) ? reviewRunForTurn.id : undefined,
            userAttachedImages: userAttachedImages,
            runtimeErrors: snapshotRuntimeErrorsForAgent(kernel),
            // A turn the user did not TYPE is a reaction to something they did - a campaign
            // created, a run finished, a panel changed. skipUserMessage is exactly that signal:
            // it is set when no user message is posted because there was none.
            reactionTurn: options?.userTypedThePrompt !== true,
        };
        markRuntimeErrorsReported(kernel);

        const turnIdForStream = uuidv4();
        const debugInfo: AssistantTurnDebugInfo = { turnId: turnIdForStream, requestBody: assistBody };
        kernel.current.setAssistantTurnDebugLog(prev => [...prev, debugInfo]);
        // A REACTION DOES NOT THINK. The user did not ask anything - they clicked something, and
        // they are watching the screen react. Announcing "Thinking" at them makes an instant reply
        // look like deliberation, which is the exact impression this was meant to remove. Real
        // telemetry from the server (searching, reading, scoring) still streams in through
        // setSessionStatus; what is suppressed is only the placeholder we invent before the first
        // token. A turn the user typed still says it is thinking, because there they ARE waiting.

        const response = await fetchApi('/api/ai/assist', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(assistBody),
            signal: currentAssistantAbortController.signal,
        });

        if (response.ok && response.body) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let messageTimestampForStream: string | null = null;
            let processedScriptForStream: ScriptInstance | null = null;
            let reviewStreamFailed = false;
            let reviewStreamHasOutput = false;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const event = JSON.parse(line.substring(6));
                            if (event.type === 'error') {
                                console.error("[useAgents] Dealer stream error:", event.payload);
                                reviewStreamFailed = true;
                                break;
                            }
                            switch (event.type) {
                                // The server appends here when a script could not be parsed, to explain
                                // the dead end. Without this case the explanation was silently dropped
                                // and the turn just ended with nothing.
                                case 'assistantMessageAppend': {
                                    dealerTurnIncomplete = true;
                                    const textToAppend = typeof event.payload === 'string' ? event.payload : '';
                                    if (messageTimestampForStream && textToAppend) {
                                        kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                            msg.timestamp === messageTimestampForStream
                                                ? { ...msg, content: (msg.content || '') + textToAppend }
                                                : msg
                                        ), sessionId);
                                    }
                                    break;
                                }
                                case 'assistantMessage':
                                    reviewStreamHasOutput ||= typeof event.payload === 'string' && !!event.payload.trim();
                                    if (!messageTimestampForStream) {
                                        messageTimestampForStream = new Date().toISOString();
                                        const content =
                                          userPreferences.actSilently !== false && processedScriptForStream
                                            ? ''
                                            : event.payload;
                                        const sessionSnapshot =
                                          kernel.current?.getInventory().ChatSession.registry[sessionId] ?? null;
                                        const messageTarget = resolveMessageTargetFromSession(sessionSnapshot);
                                        kernel.current?.updateActiveSessionMessages(
                                          prev => [
                                            ...prev,
                                            {
                                              content,
                                              role: 'assistant',
                                              timestamp: messageTimestampForStream!,
                                              agentName: 'Fater',
                                              ...messageTarget,
                                            },
                                          ],
                                          sessionId
                                        );
                                        if (processedScriptForStream) {
                                            kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                                msg.timestamp === messageTimestampForStream
                                                    ? {
                                                        ...msg,
                                                        content: (userPreferences.actSilently !== false && processedScriptForStream) ? '' : msg.content,
                                                        scriptsInstances: processedScriptForStream ? [processedScriptForStream] : [],
                                                      }
                                                    : msg
                                            ), sessionId);
                                            console.log("[Dealer SSE] Attached pre-received script to assistant message.");
                                        }
                                    }
                                    break;
                                case 'script': {
                                    if (ignoreScripts) {
                                      dealerTurnIncomplete = true;
                                      processedScriptForStream = null;
                                      break;
                                    }
                                    const scriptPayload = event.payload as ScriptDefinition;
                                    console.log("[Dealer SSE] Received script payload:", scriptPayload);
                                    if (scriptPayload?.commands?.length > 0) {
                                        reviewStreamHasOutput = true;
                                        const autoRecallProvided =
                                          typeof scriptPayload === 'object' &&
                                          scriptPayload !== null &&
                                          Object.prototype.hasOwnProperty.call(scriptPayload, 'autoRecall');
                                        // Opening/surfacing a table no longer suppresses auto-recall, the agent
                                        // keeps working after it (read/act). Runaway identical re-opens are stopped
                                        // precisely in useCommands (byte-identical-consecutive-script breaker).
                                        const resolvedAutoRecall = scriptPayload.autoRecall ?? true;
                                        const filteredCommands: UiCommand[] = [];
                                        for (const cmd of scriptPayload.commands) {
                                            if (researchRunForTurn && (researchRunForTurn.phase === 'complete' || !RESEARCH_COMMANDS.has(cmd.action_type))) continue;
                                            filteredCommands.push({
                                                id: uuidv4(),
                                                status: 'pending',
                                                action_type: cmd.action_type,
                                                parameters: cmd.parameters,
                                                description: cmd.description,
                                            });
                                        }

                                        processedScriptForStream = {
                                            id: scriptPayload.id || uuidv4(),
                                            label: scriptPayload.label,
                                            commands: filteredCommands,
                                            autorun: scriptPayload.autorun ?? true,
                                            autoRecall: resolvedAutoRecall,
                                            isAgentGenerated: true,
                                        };
                                        processedScriptForStream = removeMediaGenerationCommandsForPromptRewrite(
                                          processedScriptForStream,
                                          prompt,
                                        );
                                        processedScriptForStream = removePlanningCommandsForAnswerOnlyRequest(
                                          processedScriptForStream,
                                          prompt,
                                        );
                                        if (processedScriptForStream) {
                                          console.log('[Dealer SSE] Script autoRecall resolution:', {
                                            scriptId: processedScriptForStream.id,
                                            label: processedScriptForStream.label,
                                            rawAutoRecall: scriptPayload.autoRecall,
                                            autoRecallProvided,
                                            resolvedAutoRecall,
                                            source: autoRecallProvided ? 'payload' : 'default_true',
                                          });
                                          console.log("[Dealer SSE] Normalized script commands:", processedScriptForStream.commands.map(cmd => cmd.action_type));
                                        }
                                    } else {
                                        processedScriptForStream = null;
                                    }

                                    if (messageTimestampForStream) {
                                        kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
                                            msg.timestamp === messageTimestampForStream
                                                ? {
                                                    ...msg,
                                                    content: (userPreferences.actSilently !== false && processedScriptForStream) ? '' : msg.content,
                                                    scriptsInstances: processedScriptForStream ? [processedScriptForStream] : [],
                                                  }
                                                : msg
                                        ), sessionId);
                                        console.log("[Dealer SSE] Attached script to existing assistant message.");
                                    } else {
                                        console.warn("Script event received before assistantMessage event. Script will be attached once the message arrives.");
                                    }
                                    break;
                                }
                            }
                        } catch (e) { reviewStreamFailed = true; console.error("Failed to parse Dealer SSE event:", e); }
                    }
                }
                if (reviewRunForTurn && reviewStreamFailed) break;
            }
            if (reviewRunForTurn && (reviewStreamFailed || !reviewStreamHasOutput || buffer.trim())) {
              await reader.cancel();
              throw new Error('The review response did not finish successfully.');
            }

            if (reviewRunForTurn && useSlidesStore.getState().getAgentRun(sessionId)?.id !== reviewRunForTurn.id) return;

            if (processedScriptForStream && !messageTimestampForStream) {
              console.warn("[Dealer SSE] Script received without assistantMessage. Creating fallback assistant message container.");
              messageTimestampForStream = new Date().toISOString();
              const sessionSnapshot =
                kernel.current?.getInventory().ChatSession.registry[sessionId] ?? null;
              const messageTarget = resolveMessageTargetFromSession(sessionSnapshot);
              const fallbackMessage: Message = {
                content: '',
                role: 'assistant',
                timestamp: messageTimestampForStream,
                agentName: 'Fater',
                scriptsInstances: [processedScriptForStream],
                ...messageTarget,
              };
              kernel.current?.updateActiveSessionMessages(
                prev => [...prev, fallbackMessage],
                sessionId
              );
            }

            if (messageTimestampForStream) {
              emitAssistantMessageEventFromSession(kernel.current, sessionId, messageTimestampForStream);
            }

            if (processedScriptForStream && !processedScriptForStream.autorun) dealerTurnIncomplete = true;

            if (processedScriptForStream?.autorun) {
                dealerScriptStarted = true;
                const sessionProjectId = getProjectIdFromSessionId(sessionId);
                const dealerWorkspaceId = sessionProjectId
                  ? (kernel.current.getProjectUIWorkspaceId(sessionProjectId) || 'commander_workspace')
                  : 'commander_workspace';
                console.log(`[Dealer SSE] Autorun script "${processedScriptForStream.label}" targeting workspace "${dealerWorkspaceId}".`);
                kernel.current.runGeneratedScriptLogic(dealerWorkspaceId, processedScriptForStream, onScriptComplete, { sessionId }).catch(err => console.error("Error running dealer script:", err));
            }
        } else {
            dealerTurnFailed = true;
            // A dead response is retried like a thrown one: the task must not die with one failed
            // provider call (SRC-034).
            if (scheduleDealerTurnRetry()) return;
            kernel.current.handleError("The Dealer failed to respond.");
        }
        } catch (error: any) {
      dealerTurnFailed = true;
      if (error.name !== 'AbortError') {
        console.error("[useAgents] Dealer turn execution failed:", error);
        if (scheduleDealerTurnRetry()) return;
        kernel.current.handleError("The Dealer encountered an error.");
      }
    } finally {
      if (!dealerTurnRetryScheduled) {
        researchRunForTurn?.controller.signal.removeEventListener('abort', cancelResearchTurn);
        currentAssistantAbortController.signal.removeEventListener('abort', cancelResearch);
      }
      if (researchRunForTurn?.phase === 'complete' && !researchRunForTurn.thenReview && !dealerTurnRetryScheduled && useSlidesStore.getState().brandResearchRun?.id === researchRunForTurn.id) {
        useSlidesStore.getState().cancelBrandResearch();
      }
      const releasedTurn = !dealerTurnRetryScheduled && patchSessionRuntimeIfAbortControllerOwner(
        kernel.current,
        sessionId,
        currentAssistantAbortController,
        { abortController: null, ...(!dealerScriptStarted ? { slidesButtonIntent: null } : {}) }
      );
      // A retry keeps its status, and a cancelled turn must not clear a newer turn's feedback.
      if (releasedTurn && !dealerTurnRetryScheduled) kernel.current.setSessionStatus(sessionId, '');
      if (reviewRunForTurn && releasedTurn && !dealerScriptStarted) {
        const stopped = currentAssistantAbortController.signal.aborted || kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested;
        void useSlidesStore.getState().finishAgentRun(reviewRunForTurn, stopped ? 'stopped' : dealerTurnFailed ? 'failed' : dealerTurnIncomplete ? 'incomplete' : 'completed');
      }
      // A turn that ends its chain without a script: a research run opened by Review hands over to the review.
      if (releasedTurn && !dealerScriptStarted && !dealerTurnRetryScheduled) startReviewAfterResearch(kernel.current, sessionId);
    }

    // After a completed dealer/master turn, give the session an intelligent LLM
    // title (once, only if still default-named). Best-effort, fire-and-forget.
    void kernel.current.maybeAutoTitleSession?.(sessionId);
    // THE COMPOSER IS NOT CLEARED HERE. It used to be, at the end of EVERY completed turn, and most
    // turns are not the user's: a panel reaction, a run's landing turn, a recall. So a user typing a
    // sentence while any of those finished watched their own words vanish under the cursor.
    // Reported on 2026-08-16.
    //
    // It was redundant as well as destructive: a turn the user typed already clears the composer at
    // SEND time, in the `isUserTurn` branch of `_executeDealerTurn`, which is the only moment the
    // text has actually been consumed.
  }, [kernel, getProjectIdFromSessionId]);

  const askDealer = useCallback(async (
    sessionId: string,
    prompt: string,
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>
  ): Promise<void> => {
    if (!kernel.current) return;
    const runtime = kernel.current.readEntityRuntimeState('ChatSession', sessionId);
    const runningScriptId =
      typeof runtime.runningScriptId === 'string' ? runtime.runningScriptId.trim() : '';
    if (runningScriptId) {
      console.warn(
        `[useAgents.askDealer] Skipping dealer turn because script "${runningScriptId}" is still running in session "${sessionId}".`
      );
      kernel.current.handleError?.('A script is already running in this session. Stop it before sending a new prompt.');
      return;
    }
    errorLoopbackCountRef.current = 0;
    successLoopbackCountRef.current = 0;
    await _executeDealerTurn(sessionId, prompt, onScriptComplete);
  }, [kernel, _executeDealerTurn]);

  const executeSystemDealerTurn = useCallback(async (
    sessionId: string,
    prompt: string,
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: DealerExecutionOptions
  ): Promise<void> => {
    await _executeDealerTurn(sessionId, prompt, onScriptComplete, options);
  }, [_executeDealerTurn]);

  // The React-compiler rule sees a callback passed into this hook that may write through
  // `kernel.current` and treats it as mutating a value owned by render. `kernel` is a ref
  // container by design, which is the whole mechanism this file is built on, and the rule cannot
  // express that. Pre-existing and unchanged here; exempted with the reason rather than
  // restructured, because rewriting the kernel handle to satisfy a lint heuristic is a far larger
  // change than anything in this branch.
  // eslint-disable-next-line react-hooks/immutability
  const askAssistant = useCallback(async (
    sessionId: string,
    prompt: string,
    imageUrls?: string[],
    onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    options?: {
      skipDetachedCheck?: boolean;
      skipUserMessage?: boolean;
      queuedByUser?: boolean;
      showThinking?: boolean;
      slidesReview?: 'start' | 'restart';
      slidesProposal?: boolean;
      ignoreScripts?: boolean;
      targetWorkspaceId?: string;
      agentMode?: AgentMode;
    }
  ): Promise<void> => {
    if (!kernel.current) { return; }
    const runtime = kernel.current.readEntityRuntimeState('ChatSession', sessionId);
    if (useSlidesStore.getState().getAgentRun(sessionId)) return;
    if (options?.showThinking && runtime.abortController && !runtime.abortController.signal.aborted) return;
    const runningScriptId =
      typeof runtime.runningScriptId === 'string' ? runtime.runningScriptId.trim() : '';
    if (runningScriptId) {
      // Same stale-lock test the command runner does before executing: a lock whose script is
      // no longer in LIVE_SCRIPT_IDS was stranded (stream killed mid-script, dev-server
      // restart) and refusing on it bricks the session for the rest of the tab's life - the
      // launch click dies with only a console warn, which reads as "the button does nothing"
      // (seen live on 2026-08-24, four minutes of dead clicks on a Slides deck).
      if (!isScriptExecuting(runningScriptId)) {
        console.warn(
          `[useAgents.askAssistant] Clearing stale runningScriptId "${runningScriptId}" on session "${sessionId}" (that script is no longer executing).`
        );
        kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { runningScriptId: null });
      } else {
        console.warn(
          `[useAgents.askAssistant] Skipping assistant turn because script "${runningScriptId}" is still running in session "${sessionId}".`
        );
        kernel.current.handleError?.('A script is already running in this session. Stop it before sending a new prompt.');
        return;
      }
    }

    if (kernel.current.consecutiveRevertCounterRef) {
      // Writing a ref's `.current` through the kernel handle is the established pattern in this
      // codebase, and it is what a ref is FOR: a mutable box that does not trigger a render. The
      // React-compiler rule cannot see that `kernel.current` is a ref container rather than props,
      // so it reads this as mutating a value it owns. Pre-existing (d478c831c) and unchanged here;
      // exempted rather than restructured, because reworking kernel ref semantics to satisfy a
      // lint heuristic is a much larger change than the counter it resets.
      // eslint-disable-next-line react-hooks/immutability
      kernel.current.consecutiveRevertCounterRef.current = 0;
      console.log('[Revert Counter] Reset to 0 for new user-initiated mandate.');
    }

    const targetSession = kernel.current.getInventory().ChatSession.registry[sessionId];
    if (!targetSession) return;
    const sessionProjectId = resolveProjectIdFromChatSessionContext(targetSession);
    const derivedWorkspaceId = sessionProjectId
      ? kernel.current.getProjectUIWorkspaceId(sessionProjectId)
      : '';
    const workspaceId = options?.targetWorkspaceId || derivedWorkspaceId;
    const imagesInChatInput = kernel.current.getImagesInChatInput();
    const subjectAgentMode = resolveSessionSubjectAgentMode(targetSession);
    const effectiveAgentMode =
      subjectAgentMode ??
      options?.agentMode ??
      resolveSessionScopeDefaultAgentMode(targetSession) ??
      'commander';
    const shouldSkipUserMessage = options?.skipUserMessage ?? false;
    const shouldIgnoreScripts = options?.ignoreScripts ?? false;
    if (!shouldSkipUserMessage || options?.showThinking) {
      kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { slidesButtonIntent: null });
    }

      if (
        effectiveAgentMode === 'commander' ||
        effectiveAgentMode === 'master' ||
        effectiveAgentMode === 'doctrine' ||
        effectiveAgentMode === 'slides' ||
        effectiveAgentMode === 'pulse'
      ) {
      const slides = useSlidesStore.getState();
      const slidesHomeProjectId = targetSession.creationModule === 'slides' && targetSession.creationEntityType === 'Project'
        ? targetSession.creationEntityId?.trim() : sessionProjectId;
      const reviewRunForPreparation = (options?.slidesReview || options?.slidesProposal) && effectiveAgentMode === 'slides' && slidesHomeProjectId === slides.activeProjectId && slidesHomeProjectId
        ? options.slidesProposal ? slides.beginProposalRun(slidesHomeProjectId, sessionId)
          : slides.beginReviewRun(slidesHomeProjectId, sessionId, options.slidesReview === 'restart', prompt) : null;
      if ((options?.slidesReview || options?.slidesProposal) && !reviewRunForPreparation) return;
      errorLoopbackCountRef.current = 0;
      successLoopbackCountRef.current = 0;
      kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { isStopRequested: false });

      // Button feedback starts before attachments upload, without classifying its hidden prompt as typed.
      const candidateResearch = useSlidesStore.getState().brandResearchRun;
      const preparationResearch = candidateResearch?.sessionId === sessionId ? candidateResearch : null;
      const preparationController = options?.showThinking || preparationResearch ? new AbortController() : null;
      const cancelResearchPreparation = () => preparationController?.abort();
      preparationResearch?.controller.signal.addEventListener('abort', cancelResearchPreparation, { once: true });
      const clearPreparation = () => {
        const k = kernel.current;
        if (!preparationController || !k) return;
        const pending = k.readEntityRuntimeState('ChatSession', sessionId);
        patchSessionRuntimeIfAbortControllerOwner(k, sessionId, preparationController, {
          abortController: null,
          chatState: (pending.chatState ?? []).filter((step) => step.settled === true),
        });
      };
      if (preparationController) {
        kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { abortController: preparationController });
        preparationController.signal.addEventListener('abort', clearPreparation, { once: true });
        preparationController.signal.addEventListener('abort', () => {
          void useSlidesStore.getState().finishAgentRun(reviewRunForPreparation, 'stopped');
          if (useSlidesStore.getState().brandResearchRun?.sessionId === sessionId) useSlidesStore.getState().cancelBrandResearch();
        }, { once: true });
        kernel.current.startThinkingSequence?.(sessionId);
      }

      try {
        if (reviewRunForPreparation) {
          const inputSaved = await useSlidesStore.getState().saveDeckNow(reviewRunForPreparation.projectId);
          if (!inputSaved) throw new Error('The original review input could not be saved. Your local edits are kept; reload or resolve the save error before reviewing.');
          if (useSlidesStore.getState().getAgentRun(sessionId)?.id !== reviewRunForPreparation.id) return;
        }
        const promptImages = imagesInChatInput ? [...imagesInChatInput] : [];
        let uploadedPromptImageUrls: string[] = [];
        const sessionProjectIdForUploads = resolveProjectIdFromChatSessionContext(targetSession)
          ?? getProjectIdFromSessionId(sessionId);
        const dealerProjectOwnerId = listPromptUploadOwner(
          sessionProjectIdForUploads ? kernel.current.getInventory().Project.registry[sessionProjectIdForUploads] : undefined,
          kernel.current.getUserContext().dbUserId,
        );

        if (promptImages.length > 0) {
          const uploadResults = await Promise.all(
            promptImages.map(lockableFile => {
              const filename = `${Date.now()}${Math.random().toString(36).substring(2, 4)}.${getFileExtension(lockableFile.file.name) || 'bin'}`;
              return uploadFileToCloud(lockableFile.file, filename, lockableFile.file.type, undefined, dealerProjectOwnerId)
                .catch(error => {
                  console.error(`Error uploading dealer attachment ${lockableFile.file.name} to user storage:`, error);
                  return null;
                });
            })
          );
          uploadedPromptImageUrls = uploadResults.filter((url): url is string => !!url);
          if (uploadedPromptImageUrls.length > 0) {
            kernel.current.registerCloudAssets(uploadedPromptImageUrls, 'upload');
          }
        }

        if (preparationController?.signal.aborted ||
            (reviewRunForPreparation && useSlidesStore.getState().getAgentRun(sessionId)?.id !== reviewRunForPreparation.id) ||
            (preparationResearch && useSlidesStore.getState().brandResearchRun?.id !== preparationResearch.id)) return;

        const forwardedImageUrls = Array.isArray(imageUrls)
          ? imageUrls.filter((url): url is string => typeof url === 'string' && url.length > 0)
          : [];

        const allImageUrlsForMessage = [...uploadedPromptImageUrls, ...forwardedImageUrls];

        const trimmedPrompt = prompt.trim();
        if (!shouldSkipUserMessage && (trimmedPrompt.length > 0 || allImageUrlsForMessage.length > 0)) {
          kernel.current.addUserMessage(trimmedPrompt, allImageUrlsForMessage);
        }

        kernel.current.setChatPrompt('');
        kernel.current.setImagesInChatInput([]);

        await _executeDealerTurn(sessionId, prompt, onScriptComplete, {
          skipUserMessage: true,
          // The one place that knows whether a PERSON wrote this. `skipUserMessage` is hardcoded true
          // just above because the message is already in the transcript, so it cannot answer the
          // question downstream; this entrypoint's own flag can, and is false only for the composer.
          // A canned prompt sent by a button (SlidesReviewLauncher) passes it true and is correctly
          // not treated as something the user typed.
          userTypedThePrompt: !shouldSkipUserMessage || options?.queuedByUser === true,
          showThinking: options?.showThinking,
          slidesReviewRunId: reviewRunForPreparation && !('kind' in reviewRunForPreparation) ? reviewRunForPreparation.id : undefined,
          slidesProposalRunId: reviewRunForPreparation && 'kind' in reviewRunForPreparation ? reviewRunForPreparation.id : undefined,
          attachedImageUrls: allImageUrlsForMessage,
          ignoreScripts: shouldIgnoreScripts,
        });
      } catch (error) {
        void useSlidesStore.getState().finishAgentRun(reviewRunForPreparation, preparationController?.signal.aborted ? 'stopped' : 'failed');
        if (!preparationController) throw error;
        if (!preparationController.signal.aborted) {
          kernel.current.handleError('Could not start the assistant. Please try again.');
        }
      } finally {
        preparationResearch?.controller.signal.removeEventListener('abort', cancelResearchPreparation);
        preparationController?.signal.removeEventListener('abort', clearPreparation);
        clearPreparation();
      }
      return;
    }

    const detachedCheckWorkspace = kernel.current.getWorkspace(workspaceId);
    const isListWorkspace = getProjectStudioViewMode(kernel.current, sessionProjectId ?? detachedCheckWorkspace?.projectId) === 'marketing';
    // List is a flat output surface, not a Canvas version tree. A selected List output is context,
    // never a historical branch that needs promotion or rollback.
    if (!options?.skipDetachedCheck && !isListWorkspace) {
      const { getActiveWorkspaceVersionId, openModal } = kernel.current;
      const activeWorkspace = detachedCheckWorkspace;
      const activeVersionId = getActiveWorkspaceVersionId(workspaceId);
      
      if (activeWorkspace && activeVersionId) {
        const { validatedVersionId, versions } = activeWorkspace;
        const activeVersion = versions?.find(v => v.id === activeVersionId);

        const isDetached = activeVersionId !== validatedVersionId && activeVersion?.parentId !== validatedVersionId;

        if (isDetached) {
          openModal('DetachedStateClarification', {
            prompt,
            imageUrls,
            onScriptComplete,
            workspaceId,
            historicalVersionId: activeVersionId,
          });
          return;
        }
      }
    }

    errorLoopbackCountRef.current = 0;
    successLoopbackCountRef.current = 0;

    kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { isStopRequested: false });


    await designerTurns[designerBrainOfSession({ ...kernel.current.getState(), inventory: kernel.current.getInventory() }, sessionId, detachedCheckWorkspace?.projectId)](
      sessionId,
      workspaceId,
      prompt,
      imagesInChatInput,
      true,
      imageUrls,
      effectiveAgentMode,
      onScriptComplete,
      undefined,
      undefined,
      shouldSkipUserMessage,
      shouldIgnoreScripts,
    );
  }, [kernel, designerTurns, _executeDealerTurn, getProjectIdFromSessionId]);

  const triggerReEvaluation = useCallback(async (workspaceId: string, errorPayload?: ErrorPayload, targetSessionId?: string): Promise<void> => {
    if (!kernel.current) {
      console.error("[useAgents.triggerReEvaluation] EditorRef is not available.");
      return;
    }

    const allChatSessions = Object.values(kernel.current.getInventory().ChatSession.registry);
    const fallbackSessionId = kernel.current.getActiveChatSessionId();
    const sessionFromPayload =
      (errorPayload?.sessionId && allChatSessions.find((session: ChatSession) => session.id === errorPayload.sessionId)) ||
      (errorPayload?.scriptId
        ? allChatSessions.find((session: ChatSession) =>
            kernel.current?.getChatSessionMessages(session.id)?.some((message: Message) =>
              message.scriptsInstances?.some((script: ScriptInstance) => script.id === errorPayload.scriptId),
            ),
          )
        : undefined);

    // An explicit target session (passed by auto-recall continuations) takes
    // priority over the ACTIVE session. This is what keeps a long agent run ,
    // e.g. a Slides review, continuing after the user navigates to another page:
    // the loop follows the session that owns the work, not whatever is on screen.
    const explicitTargetSession = targetSessionId
      ? allChatSessions.find((session: ChatSession) => session.id === targetSessionId)
      : undefined;

    const resolvedSession =
      sessionFromPayload ||
      explicitTargetSession ||
      (fallbackSessionId ? allChatSessions.find((session: ChatSession) => session.id === fallbackSessionId) : undefined);

    if (!resolvedSession) {
      console.error("[useAgents.triggerReEvaluation] No session ID found.");
      return;
    }

    const sessionId = resolvedSession.id;
    const reviewRunForRecall = useSlidesStore.getState().getAgentRun(sessionId);

    if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) {
      void useSlidesStore.getState().finishAgentRun(reviewRunForRecall, 'stopped');
      console.log("[useAgents.triggerReEvaluation] Skipped due to user stop request.");
      return;
    }

    // Every path that wakes the dealer again goes through here: the runner's auto-recall AND its
    // three error loopbacks. So the depth of an app-started chain is read once, at the choke point,
    // rather than at each of the four call sites, and a chain that has had its look-up pass ends
    // here whichever path tried to continue it.
    //
    // A fresh setup reaction is exempt: it STARTS a chain rather than continuing one, and the depth
    // still on the session belongs to the previous reaction. Without this the second thing the user
    // touches in the form gets no answer at all.
    const unpromptedDepthNow = kernel.current.readEntityRuntimeState('ChatSession', sessionId).unpromptedTurnDepth ?? 0;
    if (!unpromptedChainMayContinue(unpromptedDepthNow)) {
      void useSlidesStore.getState().finishAgentRun(reviewRunForRecall, 'incomplete');
      console.log(
        `[useAgents.triggerReEvaluation] Not waking the agent: this chain was started by the app, not the user, and it is already ${unpromptedDepthNow} turns deep.`,
      );
      return;
    }

    const handleDealerRecovery = async () => {
      let dealerPrompt: string;

      if (errorPayload) {
        successLoopbackCountRef.current = 0;
        errorLoopbackCountRef.current += 1;
        console.log(`[useAgents.triggerReEvaluation] Dealer error loopback attempt ${errorLoopbackCountRef.current} of ${MAX_ERROR_LOOPBACKS}.`);

        if (errorLoopbackCountRef.current > MAX_ERROR_LOOPBACKS) {
          void useSlidesStore.getState().finishAgentRun(reviewRunForRecall, 'failed');
          console.warn(`[useAgents.triggerReEvaluation] Dealer MAX_ERROR_LOOPBACKS (${MAX_ERROR_LOOPBACKS}) exceeded for script "${errorPayload.scriptId}". Halting recovery.`);
          kernel.current?.handleError("Dealer couldn't resolve the issue after multiple attempts. Please adjust your instruction or try again later.");
          errorLoopbackCountRef.current = 0;
          return;
        }

        const paramsString = JSON.stringify(errorPayload.failedCommandParams, null, 2);
        dealerPrompt = `System Instruction (Dealer Attempt ${errorLoopbackCountRef.current} of ${MAX_ERROR_LOOPBACKS} to recover from error):
The previous attempt to execute script "${errorPayload.scriptId}" failed on command "${errorPayload.failedCommandAction}" (Command ID: ${errorPayload.commandId}).
Parameters Used: ${paramsString}
Error Message: "${errorPayload.errorMessage}"
Please diagnose the failure, adjust the plan, and produce a corrected script. Avoid repeating the same failing command with identical parameters unless you have fixed the root issue.`;
      } else {
        errorLoopbackCountRef.current = 0;
        successLoopbackCountRef.current += 1;
        const loopbackLimit = recallLimit(reviewRunForRecall ? useSlidesStore.getState().decks[reviewRunForRecall.projectId]?.doc.pages.length ?? 0 : null);
        if (successLoopbackCountRef.current > loopbackLimit) {
          void useSlidesStore.getState().finishAgentRun(reviewRunForRecall, 'incomplete');
          console.warn(
            `[useAgents.triggerReEvaluation] Dealer success loopback limit (${loopbackLimit}) reached. Halting autonomous recall.`,
          );
          kernel.current?.handleError(
            `Dealer halted after ${loopbackLimit} autonomous follow-up loops. Please provide a new instruction.`,
          );
          successLoopbackCountRef.current = 0;
          return;
        }
        console.log("[useAgents.triggerReEvaluation] Dealer re-evaluation requested without explicit error. Error loopback counter reset.");
        dealerPrompt = "";
      }

      kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { isStopRequested: false });

      try {
        await _executeDealerTurn(sessionId, dealerPrompt, undefined, {
          skipUserMessage: true,
          // A recall inherits the chain it continues, so the write ban follows the whole chain and
          // not just the turn the panel opened. The observed write was three turns downstream.
          continuesUnpromptedTurn: unpromptedDepthNow > 0,
          // EVERY continuation, whoever started the chain. `continuesUnpromptedTurn` above answers
          // a narrower question (are we inside an APP-started chain) and is false for the case that
          // matters most here: the agent waking itself to answer something a person asked.
          continuesTurn: true,
        });
      } catch (dealerError) {
        void useSlidesStore.getState().finishAgentRun(reviewRunForRecall, 'failed');
        console.error("[useAgents.triggerReEvaluation] Dealer re-evaluation failed:", dealerError);
      }
    };

    const resolvedSessionMode =
      resolveSessionSubjectAgentMode(resolvedSession) ??
      resolveSessionScopeDefaultAgentMode(resolvedSession) ??
      'commander';

    // Master (and doctrine) sessions ride the dealer transport, their
    // auto-recall continuation is a dealer-style turn too. The director path
    // below is Studio-only (it describes a canvas workspace, which these
    // sessions don't have). Slides and doctrine ride the same transport.
    if (
      resolvedSessionMode === 'commander' ||
      resolvedSessionMode === 'master' ||
      resolvedSessionMode === 'doctrine' ||
      resolvedSessionMode === 'slides' ||
      resolvedSessionMode === 'pulse'
    ) {
      await handleDealerRecovery();
      return;
    }

    let systemPromptForTurn: string;
    const progressiveStepRetryCount = kernel.current?.consecutiveRevertCounterRef?.current ?? 0;
    if (progressiveStepRetryCount > 0) {
      console.log(`[useAgents.triggerReEvaluation] Progressive retry count detected: ${progressiveStepRetryCount}.`);
    }

    if (errorPayload) {
      errorLoopbackCountRef.current += 1;
      console.log(`[useAgents.triggerReEvaluation] Error loopback attempt ${errorLoopbackCountRef.current} of ${MAX_ERROR_LOOPBACKS}.`);

      if (errorLoopbackCountRef.current > MAX_ERROR_LOOPBACKS) {
        console.warn(`[useAgents.triggerReEvaluation] MAX_ERROR_LOOPBACKS (${MAX_ERROR_LOOPBACKS}) exceeded for script "${errorPayload.scriptId}". Halting recovery.`);
        errorLoopbackCountRef.current = 0;
        kernel.current?.handleError("Assistant encountered an issue it could not resolve after multiple attempts. Please try a different approach or contact support.");
        return;
      }

      const paramsString = JSON.stringify(errorPayload.failedCommandParams, null, 2);
      systemPromptForTurn = `System Instruction (Attempt ${errorLoopbackCountRef.current} of ${MAX_ERROR_LOOPBACKS} to recover from error):
The previous attempt to execute a step in script "${errorPayload.scriptId}" (Command ID: ${errorPayload.commandId}) failed.
Command Action: "${errorPayload.failedCommandAction}"
Parameters Used: ${paramsString}
Error Message: "${errorPayload.errorMessage}"
Please analyze this failure. Provide a revised plan or ask for clarification. Avoid repeating the exact same failing command with the same parameters unless the underlying issue is believed to be resolved.`;
    } else {
      errorLoopbackCountRef.current = 0; 
      console.log("[useAgents.triggerReEvaluation] Non-error re-evaluation requested. Error loopback counter reset.");
      systemPromptForTurn = "System Instruction: The previous script/action has completed. Please re-evaluate the current state and decide on the next steps or await user input.";
    }

    kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { isStopRequested: false });

    // A recall continues the legacy turn whose script asked for it, whatever brain the next message runs on:
    // the jnn brain's actions are single-execution scripts, which wake no assistant.
    await designerTurns.legacy(
      sessionId,
      workspaceId,
      systemPromptForTurn,
      [],
      false,
      [],
      'director',
      undefined,
      errorPayload,
      progressiveStepRetryCount,
      false,
      false,
    );

  }, [kernel, designerTurns, _executeDealerTurn, getProjectIdFromSessionId]);

  const generateOriginalConcepts = useCallback(async (
    prompt: string,
    ideasCount: number,
    options?: { sessionId?: string; generateMultiplier?: number; bypassJudge?: boolean; abortSignal?: AbortSignal }
  ): Promise<GenerateOriginalConceptsResponse> => {
    const normalizedPrompt = typeof prompt === 'string' ? prompt.trim() : '';
    if (!normalizedPrompt) {
      throw new Error('generateOriginalConcepts: "prompt" must be a non-empty string.');
    }

    const numericIdeasCount =
      typeof ideasCount === 'number'
        ? ideasCount
        : Number(ideasCount);
    const normalizedIdeasCount = Math.floor(numericIdeasCount);

    if (!Number.isFinite(normalizedIdeasCount) || normalizedIdeasCount <= 0) {
      throw new Error('generateOriginalConcepts: "ideasCount" must be a positive integer.');
    }

    const cappedIdeasCount = Math.min(normalizedIdeasCount, 20);
    const requestBody: GenerateOriginalConceptsRequestBody = {
      prompt: normalizedPrompt,
      ideasCount: cappedIdeasCount,
    };

    const sessionId =
      typeof options?.sessionId === 'string' && options.sessionId.trim().length > 0
        ? options.sessionId.trim()
        : kernel.current?.getActiveChatSessionId() ?? undefined;
    if (sessionId) {
      requestBody.sessionId = sessionId;
    }
    if (typeof options?.generateMultiplier === 'number' && Number.isFinite(options.generateMultiplier)) {
      requestBody.generateMultiplier = options.generateMultiplier;
    }
    if (typeof options?.bypassJudge === 'boolean') {
      requestBody.bypassJudge = options.bypassJudge;
    }

    const response = await fetch('/api/ai/original-concepts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: options?.abortSignal,
    });

    let responseBody: unknown = null;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = null;
    }

    if (!response.ok) {
      const errorMessage =
        responseBody && typeof responseBody === 'object' && typeof (responseBody as { message?: unknown }).message === 'string'
          ? (responseBody as { message: string }).message
          : `Request failed with status ${response.status}`;
      throw new Error(`generateOriginalConcepts: ${errorMessage}`);
    }

    const ideas = Array.isArray((responseBody as { ideas?: unknown })?.ideas)
      ? (responseBody as { ideas: unknown[] }).ideas
          .filter((idea): idea is string => typeof idea === 'string' && idea.trim().length > 0)
          .map((idea) => idea.trim())
      : [];
    const results = Array.isArray((responseBody as { results?: unknown })?.results)
      ? (responseBody as { results: unknown[] }).results
          .filter((entry) => entry && typeof entry === 'object')
          .map((entry) => {
            const record = entry as Record<string, unknown>;
            const combinationId =
              typeof record.combinationId === 'string' && record.combinationId.trim().length > 0
                ? record.combinationId.trim()
                : 'unknown';
            const score =
              typeof record.score === 'number' && Number.isFinite(record.score)
                ? record.score
                : null;
            const concept =
              typeof record.concept === 'string' && record.concept.trim().length > 0
                ? record.concept.trim()
                : '';
            return { combinationId, score, concept };
          })
          .filter((entry) => entry.concept.length > 0)
      : undefined;
    const costSummary =
      responseBody && typeof responseBody === 'object' && 'costSummary' in (responseBody as Record<string, unknown>)
        ? (responseBody as GenerateOriginalConceptsResponse).costSummary
        : undefined;
    const pipelineDetails =
      responseBody && typeof responseBody === 'object' && 'pipelineDetails' in (responseBody as Record<string, unknown>)
        ? (responseBody as GenerateOriginalConceptsResponse).pipelineDetails
        : undefined;

    return { ideas, results, costSummary, pipelineDetails };
  }, [kernel]);

  return useMemo(
    () => ({
      askAssistant,
      askDealer,
      executeSystemDealerTurn,
      generateOriginalConcepts,
      resolveDetachedState,
      triggerReEvaluation,
    }),
    [askAssistant, askDealer, executeSystemDealerTurn, generateOriginalConcepts, resolveDetachedState, triggerReEvaluation],
  );
}
